import React, { FC } from 'react';
import {
    SkeletonBlock,
    SkeletonPageHeader,
    SkeletonPageShell,
    SkeletonResponsiveGrid,
    SkeletonRow,
    SkeletonStack,
    SkeletonSurface
} from 'src/components/widgets/skeleton';
import styled from 'styled-components';

const DashboardSkeletonShell = styled(SkeletonPageShell)`
    max-width: 1600px;
    margin: 0 auto;
    padding-top: 14px;
`;

const ColumnsSkeleton = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 330px;
    gap: 22px;
    align-items: start;

    @media screen and (max-width: 1080px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const LeftColumnSkeleton = styled.div`
    display: flex;
    flex-direction: column;
`;

const RailSkeleton = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

// The onboarding hero (title + subtitle) that leads the left column.
const HeroSkeleton = styled(SkeletonStack)`
    margin-bottom: 18px;
`;

// The compressed AI assistant bar above the widgets.
const AiBarSkeleton = styled(SkeletonSurface)`
    height: 46px;
    border-radius: 13px;
    margin-bottom: 14px;
`;

const WidgetsGridSkeleton = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    grid-auto-rows: 184px;
    gap: 12px;
`;

const WidgetSkeletonCard = styled(SkeletonSurface)`
    padding: 14px 15px 13px;
    border-radius: 14px;
    height: 100%;
`;

const WidgetSkeletonHead = styled(SkeletonRow)`
    align-items: center;
    margin-bottom: 11px;
`;

const DividerSkeleton = styled.hr`
    border: 0;
    border-top: 1px solid #e4e2dc;
    margin: 26px 0 14px;
`;

const AnalyticsCardSkeleton = styled(SkeletonSurface)`
    padding: 14px 16px;
    border-radius: 14px;
`;

const ChartSkeleton = styled(SkeletonBlock)`
    margin-top: 12px;
`;

const SuggestionCardSkeleton = styled(SkeletonSurface)`
    margin-top: 22px;
    padding: 14px 16px 15px;
    border-radius: 14px;
`;

const PanelSurfaceSkeleton = styled(SkeletonSurface)`
    border-radius: 14px;
    padding: 14px 15px;
`;

const RowSkeleton = styled(SkeletonRow)`
    align-items: flex-start;
    padding: 8px 0;

    & + & {
        border-top: 1px solid #e4e2dc;
    }
`;

const ResourcesSkeleton = styled(SkeletonRow)`
    justify-content: flex-end;
    flex-wrap: wrap;
    min-width: 0;
`;

export const DashboardPageSkeleton: FC = () => (
    <DashboardSkeletonShell aria-hidden='true'>
        <SkeletonPageHeader $margin='0 0 14px'>
            <SkeletonRow $gap='12px' $align='center'>
                <SkeletonBlock $width='min(260px, 100%)' $height='24px' />
                <SkeletonBlock $width='min(300px, 100%)' $height='16px' />
            </SkeletonRow>
            <ResourcesSkeleton $gap='5px'>
                {Array.from({ length: 6 }).map((_, index) => (
                    <SkeletonBlock key={index} $width={index % 2 === 0 ? '112px' : '94px'} $height='22px' $radius='999px' />
                ))}
            </ResourcesSkeleton>
        </SkeletonPageHeader>

        <ColumnsSkeleton>
            <LeftColumnSkeleton>
                {/* Hero: title + subtitle */}
                <HeroSkeleton $gap='8px'>
                    <SkeletonBlock $width='min(340px, 90%)' $height='24px' />
                    <SkeletonBlock $width='min(440px, 100%)' $height='16px' />
                </HeroSkeleton>

                {/* AI assistant bar */}
                <AiBarSkeleton />

                {/* Widgets grid */}
                <WidgetsGridSkeleton>
                    {Array.from({ length: 4 }).map((_, index) => (
                        <WidgetSkeletonCard key={index}>
                            <WidgetSkeletonHead $gap='9px'>
                                <SkeletonBlock $width='28px' $height='28px' $radius='8px' />
                                <SkeletonBlock $width={index % 2 === 0 ? '120px' : '92px'} $height='14px' />
                            </WidgetSkeletonHead>
                            <SkeletonStack $gap='10px'>
                                <SkeletonBlock $width='64px' $height='26px' />
                                <SkeletonBlock $width='85%' $height='16px' $radius='999px' />
                            </SkeletonStack>
                        </WidgetSkeletonCard>
                    ))}
                </WidgetsGridSkeleton>

                {/* Analytics */}
                <DividerSkeleton />
                <AnalyticsCardSkeleton>
                    <SkeletonRow $gap='10px' $justify='space-between' $align='center'>
                        <SkeletonBlock $width='120px' $height='16px' />
                        <SkeletonBlock $width='150px' $height='24px' $radius='8px' />
                    </SkeletonRow>
                    <ChartSkeleton $width='100%' $height='150px' $radius='10px' />
                </AnalyticsCardSkeleton>

                {/* Setup suggestion card */}
                <SuggestionCardSkeleton>
                    <SkeletonStack $gap='8px'>
                        <SkeletonBlock $width='120px' $height='12px' />
                        <SkeletonBlock $width='min(320px, 80%)' $height='18px' />
                        <SkeletonBlock $width='min(420px, 100%)' $height='14px' />
                        <SkeletonRow $gap='12px' $margin='4px 0 0'>
                            <SkeletonBlock $width='110px' $height='30px' $radius='6px' />
                            <SkeletonBlock $width='120px' $height='16px' />
                        </SkeletonRow>
                    </SkeletonStack>
                </SuggestionCardSkeleton>
            </LeftColumnSkeleton>

            <RailSkeleton>
                {/* Setup / success card */}
                <PanelSurfaceSkeleton>
                    <SkeletonRow $gap='10px' $justify='space-between' $align='center' $margin='0 0 10px'>
                        <SkeletonBlock $width='140px' $height='16px' />
                        <SkeletonBlock $width='70px' $height='12px' />
                    </SkeletonRow>
                    <SkeletonRow $gap='3px' $margin='0 0 12px'>
                        {Array.from({ length: 3 }).map((_, index) => (
                            <SkeletonBlock key={index} $width='100%' $height='3px' $radius='2px' />
                        ))}
                    </SkeletonRow>
                    {Array.from({ length: 3 }).map((_, index) => (
                        <RowSkeleton key={index} $gap='9px'>
                            <SkeletonBlock $width='15px' $height='15px' $radius='999px' />
                            <SkeletonStack $gap='5px' $flex='1'>
                                <SkeletonBlock $width={index === 0 ? '55%' : '70%'} $height='13px' />
                                <SkeletonBlock $width='90%' $height='11px' />
                            </SkeletonStack>
                        </RowSkeleton>
                    ))}
                </PanelSurfaceSkeleton>

                {/* Plan card (trial callout + usage) */}
                <PanelSurfaceSkeleton>
                    <SkeletonRow $gap='10px' $justify='space-between' $align='center' $margin='0 0 14px'>
                        <SkeletonBlock $width='120px' $height='16px' />
                        <SkeletonBlock $width='72px' $height='24px' $radius='6px' />
                    </SkeletonRow>
                    <SkeletonResponsiveGrid $columns='repeat(2, minmax(0, 1fr))' $gap='15px 22px' $tabletColumns='repeat(2, minmax(0, 1fr))'>
                        {Array.from({ length: 4 }).map((_, index) => (
                            <SkeletonStack key={index} $gap='6px'>
                                <SkeletonBlock $width='100%' $height='12px' />
                                <SkeletonBlock $width='100%' $height='4px' $radius='3px' />
                            </SkeletonStack>
                        ))}
                    </SkeletonResponsiveGrid>
                </PanelSurfaceSkeleton>
            </RailSkeleton>
        </ColumnsSkeleton>
    </DashboardSkeletonShell>
);
